import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { useQuery } from "@tanstack/react-query";
import PageLayout from "@/components/layout/PageLayout";
import SearchableCustomerSelect from "@/components/shared/SearchableCustomerSelect";
import FormActions from "@/components/shared/FormActions";

const CreateProjectPage = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, last_name, customer_type, business_name, representative_first_name, representative_last_name');

      if (error) throw error;
      return (data || []).sort((a, b) =>
        getCustomerDisplayName(a).localeCompare(getCustomerDisplayName(b))
      );
    },
  });

  const validateForm = () => {
    if (!projectName.trim()) {
      notify.error("Project name is required.");
      return false;
    }
    if (!customerId) {
      notify.error("Please select a customer.");
      return false;
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      notify.error("End date cannot be before start date.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profileData?.company_id) {
        throw new Error('User company not found');
      }

      const { data, error } = await supabase
        .from("projects")
        .insert({
          name: projectName.trim(),
          description: description.trim() || null,
          customer_id: customerId,
          company_id: profileData.company_id,
          expected_start_date: startDate || null,
          expected_end_date: endDate || null,
          created_by: user.id,
          estimate_id: null,
        })
        .select()
        .single();

      if (error) throw error;

      notify.success("Project created successfully!");
      navigate(`/projects/${data.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      notify.error("Failed to create project. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout maxWidth="lg" padding="md">
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/projects">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Projects
            </Link>
          </Button>
        </div>

        <div>
          <h1 className="text-2xl font-bold font-montserrat">Create New Project</h1>
          <p className="text-sm text-muted-foreground">
            Set up a new project and link it to a customer
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium mb-1" htmlFor="projectName">
              Project Name *
            </label>
            <Input
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name"
              required
            />
          </div>

          <div>
            <label className="block font-medium mb-1" htmlFor="customer">
              Customer *
            </label>
            <SearchableCustomerSelect
              value={customerId}
              onValueChange={setCustomerId}
              customers={customers}
              placeholder="Select a customer..."
            />
          </div>

          <div>
            <label className="block font-medium mb-1" htmlFor="description">
              Description
            </label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter project description (optional)"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1" htmlFor="startDate">
                Expected Start Date
              </label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block font-medium mb-1" htmlFor="endDate">
                Expected End Date
              </label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
              />
            </div>
          </div>

          <FormActions
            onCancel={() => navigate("/projects")}
            isSubmitting={isSubmitting}
            submitLabel="Create Project"
            submittingLabel="Creating..."
          />
        </form>
      </div>
    </PageLayout>
  );
};

export default CreateProjectPage;
